import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAuftraegeMutation } from '../../api/AuftragAPI';
import { IAuftrag, ICalendarEvent } from '../../types/Types';
import { formatToEuroDate } from '../../util/DateUtils';
import ButtonStartNaviComponent from '../ButtonStartNaviComponent/ButtonStartNaviComponent';
import styles from './calendarcomponent.module.scss';
import { useWebSocket } from '../../util/Websocket';
import ButtonCopyComponent from '../buttons/ButtonCopyComponent';
import ButtonCallNumberComponent from '../buttons/ButtonCallNumberComponent copy';
import { useGetCalendarEventListeMutation } from '../../api/CalendarEventAPI';

type ListAuftragOrEvent = {
  type: 'auftrag' | 'event';
  auftrag: IAuftrag | undefined;
  event: ICalendarEvent | undefined;
}

/**
 * @author noone
 */
const CalendarComponent: React.FC = () => {

  const [getAuftraegeAPI] = useGetAuftraegeMutation();
  const [getCalendarEventListeAPI] = useGetCalendarEventListeMutation();

  const [list, setList] = useState<ListAuftragOrEvent[]>([]);

  const [auftraege, setAuftraege] = useState<IAuftrag[]>([]);
  //const [bis, setBis] = useState<Date>(new Date());
  const [getTagZumAnzeigen, setTagZumAnzeigen] = useState<Date>(new Date());
  //const [tage, setTage] = useState<Map<number, Date>>(new Map());
  const [selectedAppointment, setSelectedAppointment] = useState<ListAuftragOrEvent | null>(null);
  const [calendarEventListe, setCalendarEventListe] = useState<ICalendarEvent[]>();
  const { messages, send } = useWebSocket("termin");

  const [getVon, setVon] = useState<Date>(() => {
    const montag = new Date();
    const diff = (montag.getDay() + 6) % 7;
    montag.setDate(montag.getDate() - diff);
    montag.setUTCHours(0, 0, 0, 0);
    return montag;
  });

  const bis = useMemo(() => {
    const ende = new Date(getVon);
    ende.setDate(ende.getDate() + 6);
    ende.setUTCHours(23, 59, 59, 999);
    return ende;
  }, [getVon]);

  const tage = useMemo(() => {
    const newTage = new Map<number, Date>();
    for (let i = 0; i < 5; i++) {
      const tag = new Date(getVon);
      tag.setDate(getVon.getDate() + i); // <-- hier werden die Folgetage erzeugt
      newTage.set(i, tag);
    }
    return newTage;
  }, [getVon]);

  const lade = useCallback(() => {
    getAuftraegeAPI({ status: ["TERMINIERT", "FERTIG", "FAKTURIERBAR"], terminVon: getVon, terminBis: bis }).unwrap()
      .then(data => setAuftraege(data))
      .catch((err) => console.log(err));

    getCalendarEventListeAPI({ von: getVon, bis: bis }).unwrap()
      .then((data) => setCalendarEventListe(data))
      .catch((err) => console.log(err));
  }, [getAuftraegeAPI, getCalendarEventListeAPI, getVon, bis]);

  const eineWocheZurück = () => {
    setVon(new Date(getVon.setDate(getVon.getDate() - 7)));
  }

  const eineWocheSpäter = () => {
    setVon(new Date(getVon.setDate(getVon.getDate() + 7)));
  }

  function isSameDay(date1: Date | null, date2: Date): boolean {
    return formatToEuroDate(date1) === formatToEuroDate(date2);
  }

  const openAppointmentDialog = (item: ListAuftragOrEvent) => {
    setSelectedAppointment(item);
  }

  const closeAppointmentDialog = () => {
    setSelectedAppointment(null);
  }

  // Stunden 7..22
  const hours = Array.from({ length: 16 }, (_, i) => i + 7);
  const HOUR_HEIGHT = 80; // px per hour row, used to compute quarter-hour positions

  const getAppointmentsForDayAndHour = (day: Date, hour: number) => {
    return list.filter(a => {
      if (a.type == 'event' && a.event !== undefined && a.event.terminISO) {
        const d = new Date(a.event.terminISO);
        return isSameDay(d, day) && d.getHours() === hour;
      } else if (a.type == 'auftrag' && a.auftrag !== undefined && a.auftrag.aktuellerTermin) {
        const d = new Date(a.auftrag.aktuellerTermin);
        return isSameDay(d, day) && d.getHours() === hour;
      }
    });
  }

  // Berechne die Höhe basierend auf der Dauer in Minuten
  const getAppointmentHeight = (durationMinutes: number): number => {
    return (durationMinutes / 60) * HOUR_HEIGHT;
  }

  // Gruppiere Appointments mit gleicher Start-Zeit und berechne ihre Position/Breite
  const getAppointmentPositioning = (apps: ListAuftragOrEvent[], appointmentIndex: number) => {
    if (apps.length <= 1) {
      return { left: 6, width: 'calc(100% - 12px)', position: 0, count: 1 };
    }

    // Berechne für jedes Appointment Start- und End-Minute (relativ zur Stunde)
    type Item = { idx: number; start: number; end: number };
    const items: Item[] = apps.map((a, i) => {
      const d = new Date((a.auftrag !== undefined) ? a.auftrag!.aktuellerTermin as Date : a.event!.terminISO);
      const start = d.getMinutes();
      const end = start + ((a.auftrag !== undefined) ? a.auftrag!.dauerInMinuten : a.event!.dauerInMinuten || 0);
      return { idx: i, start, end };
    });

    // Sortiere nach Startzeit (frühere zuerst)
    const sorted = items.slice().sort((x, y) => x.start - y.start || y.end - x.end);

    // Greedy-Zuweisung auf Spalten: jede Spalte merkt sich das Ende des letzten Events
    const columnsEnd: number[] = [];
    const assigned: number[] = new Array(items.length).fill(0);

    for (const it of sorted) {
      let placed = false;
      for (let c = 0; c < columnsEnd.length; c++) {
        if (it.start >= columnsEnd[c]) {
          assigned[it.idx] = c;
          columnsEnd[c] = it.end;
          placed = true;
          break;
        }
      }
      if (!placed) {
        const newCol = columnsEnd.length;
        columnsEnd.push(it.end);
        assigned[it.idx] = newCol;
      }
    }

    const colIndex = assigned[appointmentIndex];
    const colCount = columnsEnd.length;
    const widthPercent = 100 / colCount;
    const leftPercent = colIndex * widthPercent;

    const margin = 6; // px Marge zwischen Appointments
    const width = `calc(${widthPercent}% - ${margin}px)`;
    const left = `calc(${leftPercent}% + ${colIndex > 0 ? margin : 6}px)`;

    return { left, width, position: colIndex, count: colCount };
  }

  useEffect(() => {
    lade();
  }, [lade]);

  useEffect(() => {
    if (messages.length === 0) return;
    const satz = messages.at(-1);
    if (satz?.type === "TERMINAENDERUNG") {
      lade();
    }
  }, [messages, lade]);

  useEffect(() => {
    if (auftraege === undefined) return;
    var list1: ListAuftragOrEvent[] = [];
    auftraege.map((auftrag) => list1.push({ type: 'auftrag', auftrag, event: undefined }));
    setList(prev => [...prev.filter((item) => item.type !== 'auftrag'), ...list1])
  }, [auftraege]);

  useEffect(() => {
    if (calendarEventListe === undefined) return;
    var list1: ListAuftragOrEvent[] = [];
    calendarEventListe.map((item) => list1.push({ type: 'event', auftrag: undefined, event: item }));
    setList(prev => [...prev.filter((item) => item.type !== 'event'), ...list1])
  }, [calendarEventListe]);


  return (
    <>

      <p>
        Anzahl Aufträge im Zeitraum von {formatToEuroDate(getVon)} bis {formatToEuroDate(bis)}: {auftraege.length}
      </p>

      <button className="btn btn-light" onClick={eineWocheZurück}><i className="bi bi-arrow-left"></i>Früher</button>
      <button className="btn btn-light" onClick={eineWocheSpäter}>Später<i className="bi bi-arrow-right"></i></button>

      <div className={styles.aussendiensttage}>
        {Array.from(tage.entries()).map(([index, date]) => (
          <div key={index} className={styles.dayColumn + " card"}>
            <div className={styles.dayHeader}>
              <button className="btn btn-secondary m-1" onClick={() => setTagZumAnzeigen(date)}>
                {(index === 0) ? "Montag" : index === 1 ? "Dienstag" : index === 2 ? "Mittwoch" : index === 3 ? "Donnerstag" : index === 4 ? "Freitag" : "Unbekannt"}<br />
                {formatToEuroDate(date)}
              </button>
            </div>

            <div className={styles.hoursContainer}>
              {hours.map(h => {
                const apps = getAppointmentsForDayAndHour(date, h);
                return (
                  <div key={h} className={styles.hourRow}>
                    <div className={styles.hourLabel}>{h}:00</div>
                    <div className={styles.hourContent}>
                      {apps.length === 0 && <div className={styles.hourEmpty}></div>}
                      {apps.map((item, idx) => {
                        const d = new Date((item.auftrag !== undefined) ? item.auftrag!.aktuellerTermin as Date : item.event!.terminISO);
                        const minutes = d.getMinutes();
                        // position within the hour in pixels
                        const top = (minutes / 60) * HOUR_HEIGHT;
                        // height basierend auf der Dauer in Minuten
                        const height = getAppointmentHeight((item.auftrag !== undefined) ? item.auftrag!.dauerInMinuten : item.event!.dauerInMinuten);
                        // Positionierung für nebeneinander angeordnete Termine
                        const positioning = getAppointmentPositioning(apps, idx);
                        return (
                          <div
                            key={idx}
                            className={styles.appointment}
                            style={{
                              backgroundColor: (item.auftrag !== undefined) ? item.auftrag?.colorProfile + "33" : "#f0f0f0",
                              borderLeft: "4px solid " + (item.auftrag !== undefined ? item.auftrag?.colorProfile : "#000"),
                              position: 'absolute',
                              left: positioning.left,
                              top: top,
                              width: positioning.width,
                              height: height,
                              zIndex: 1
                            }}
                          >
                            <div className={styles.appointmentHeader}>
                              <div className={styles.appointmentTitle}>
                                {d.getHours().toString().padStart(2, '0')}:{d.getMinutes().toString().padStart(2, '0')} - {item.auftrag?.stadt_lieferadresse} {item.auftrag?.name_endkunde}
                              </div>
                              {item.event &&
                                <>{item.event.text}</>
                              }

                              <button
                                className={`btn btn-sm ${styles.expandButton}`}
                                onClick={(event: FormEvent) => {
                                  event.stopPropagation();
                                  openAppointmentDialog(item);
                                }}
                                title="Details anzeigen"
                              >
                                <i className="bi bi-arrows-fullscreen"></i>
                              </button>

                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>

        ))}
      </div>

      {selectedAppointment && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
          onClick={closeAppointmentDialog}
        >
          <div
            style={{
              width: 'min(700px, 100%)',
              maxHeight: '80vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
              border: '4px solid ' + selectedAppointment.auftrag?.colorProfile + '',
              padding: 20,
              position: 'relative',
            }}
            onClick={(event: FormEvent) => event.stopPropagation()}
          >
            <button
              type="button"
              className="btn btn-close"
              aria-label="Schließen"
              onClick={closeAppointmentDialog}
              style={{ position: 'absolute', top: 12, right: 12 }}
            />

            <div style={{ marginRight: 40, marginBottom: 16 }}>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={closeAppointmentDialog}
              >
                Schließen
              </button>
            </div>

            {selectedAppointment.auftrag && (
              <>
                <h4>{selectedAppointment.auftrag.name_endkunde}</h4>
                <p><strong>Ort:</strong> {selectedAppointment.auftrag.stadt_lieferadresse}</p>
                <p><strong>Adresse:</strong> {selectedAppointment.auftrag.strasse_lieferadresse} {selectedAppointment.auftrag.hausnummer_lieferadresse}</p>
                <p><strong>Termin:</strong> {selectedAppointment.auftrag.aktuellerTermin ? new Date(selectedAppointment.auftrag.aktuellerTermin).toLocaleString() : ''}</p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                  <ButtonStartNaviComponent auftrag={selectedAppointment.auftrag} />

                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      window.open(
                        "./.auftragdetails?uid=" + selectedAppointment.auftrag?.uid + "&backbuttonClose=true",
                        "_blank"
                      );
                    }}
                  >
                    <i className='bi bi-pencil-fill'></i>
                  </button>

                  <ButtonCopyComponent
                    textToCopy={""
                      + selectedAppointment.auftrag.strasse_lieferadresse + " "
                      + selectedAppointment.auftrag.hausnummer_lieferadresse + "\n"
                      + selectedAppointment.auftrag.postleitzahl_lieferadresse + " "
                      + selectedAppointment.auftrag.stadt_lieferadresse}
                    textToDisplay='Kopiere Adresse'
                  />

                  {selectedAppointment.auftrag.mobil1_lieferadresse && selectedAppointment.auftrag.mobil1_lieferadresse !== "" && (
                    <ButtonCopyComponent
                      textToCopy={selectedAppointment.auftrag.mobil1_lieferadresse}
                      textToDisplay='Kopiere Mobil1'
                    />
                  )}

                  {selectedAppointment.auftrag.mobil1_lieferadresse && selectedAppointment.auftrag.mobil1_lieferadresse !== "" && (
                    <ButtonCallNumberComponent numberToCall={selectedAppointment.auftrag.mobil1_lieferadresse} />
                  )}
                </div>
              </>
            )}

            {selectedAppointment.event && (
              <>
                <h4>{selectedAppointment.event.text}</h4>
                <p><strong>Termin:</strong> {selectedAppointment.event.terminISO ? new Date(selectedAppointment.event.terminISO).toLocaleString() : ''}</p>
                <p><strong>Dauer:</strong> {selectedAppointment.event.dauerInMinuten} Minuten</p>
              </>
            )}
          </div>
        </div>
      )}

    </>
  );

}

export default CalendarComponent;